import React, { useState } from 'react';
import './AnalysisPieChart.css';

/**
 * Donut-/Kreisdiagramm fuer Auswertungen.
 * Slices werden nach value absteigend sortiert.
 * Slices unter othersThreshold (Anteil 0..1) werden zu "Sonstige" zusammengefasst (ausser bei <= 1 Slice).
 *
 * @param {{
 *   slices: Array<{ key: string, label: string, value: number, color?: string, count?: number }>,
 *   formatValue: (n: number) => string,
 *   centerLabel?: string,
 *   centerValue?: string,
 *   darkMode?: boolean,
 *   size?: number,
 *   onSliceClick?: (key: string) => void,
 *   othersThreshold?: number,
 *   othersLabel?: string,
 *   ariaLabel?: string,
 * }} props
 */
function AnalysisPieChart({
  slices,
  formatValue,
  centerLabel,
  centerValue,
  darkMode = false,
  size = 220,
  onSliceClick,
  othersThreshold = 0.02,
  othersLabel = 'Sonstige',
  ariaLabel = 'Kreisdiagramm',
}) {
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const safe = (Array.isArray(slices) ? slices : []).filter(s => s && (s.value || 0) > 0);
  const total = safe.reduce((s, x) => s + (x.value || 0), 0);

  if (safe.length === 0 || total <= 0) {
    return (
      <div className={`analysis-pie-chart ${darkMode ? 'dark-mode' : ''}`} style={{ width: size, minHeight: size }}>
        <div className="analysis-pie-empty">Keine Daten fuer eine Darstellung.</div>
      </div>
    );
  }

  // Sortiere absteigend; gruppiere kleine Slices als "Sonstige"
  const sorted = [...safe].sort((a, b) => (b.value || 0) - (a.value || 0));
  const big = [];
  const smallList = [];
  sorted.forEach(s => {
    const ratio = total > 0 ? (s.value || 0) / total : 0;
    if (ratio < othersThreshold && sorted.length > 2) {
      smallList.push(s);
    } else {
      big.push(s);
    }
  });
  const renderedSlices = [...big];
  if (smallList.length > 1) {
    const sumVal = smallList.reduce((s, x) => s + (x.value || 0), 0);
    const sumCount = smallList.reduce((s, x) => s + (x.count || 0), 0);
    renderedSlices.push({
      key: '__others__',
      label: othersLabel,
      value: sumVal,
      count: sumCount,
      color: darkMode ? '#6b7280' : '#9ca3af',
      _isOthers: true,
      _members: smallList,
    });
  } else if (smallList.length === 1) {
    renderedSlices.push(smallList[0]);
  }

  // Single-Slice-Sonderfall: kein Diagramm, nur Wertanzeige
  if (renderedSlices.length === 1) {
    const only = renderedSlices[0];
    return (
      <div className={`analysis-pie-chart ${darkMode ? 'dark-mode' : ''}`} style={{ width: size, minHeight: size }}>
        <div className="analysis-pie-single">
          <div className="analysis-pie-single-color" style={{ backgroundColor: only.color || '#667eea' }} />
          <div className="analysis-pie-single-label">{only.label}</div>
          <div className="analysis-pie-single-value">{formatValue(only.value || 0)}</div>
          {only.count != null && <div className="analysis-pie-single-count">{only.count} Messpunkt(e)</div>}
        </div>
      </div>
    );
  }

  // Geometrie: Donut
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 6;
  const innerR = outerR * 0.62;
  const fallbackPalette = darkMode
    ? ['#60a5fa', '#fbbf24', '#34d399', '#f472b6', '#a78bfa', '#22d3ee', '#fb7185', '#facc15', '#4ade80', '#c084fc']
    : ['#3b82f6', '#f59e0b', '#10b981', '#ec4899', '#8b5cf6', '#06b6d4', '#ef4444', '#eab308', '#22c55e', '#a855f7'];

  // Slice-Pfade berechnen
  let acc = 0;
  const pathSlices = renderedSlices.map((s, i) => {
    const ratio = (s.value || 0) / total;
    const startAng = acc * 2 * Math.PI - Math.PI / 2;
    acc += ratio;
    const endAng = acc * 2 * Math.PI - Math.PI / 2;
    const largeArc = ratio > 0.5 ? 1 : 0;
    const x1 = cx + outerR * Math.cos(startAng);
    const y1 = cy + outerR * Math.sin(startAng);
    const x2 = cx + outerR * Math.cos(endAng);
    const y2 = cy + outerR * Math.sin(endAng);
    const xi1 = cx + innerR * Math.cos(endAng);
    const yi1 = cy + innerR * Math.sin(endAng);
    const xi2 = cx + innerR * Math.cos(startAng);
    const yi2 = cy + innerR * Math.sin(startAng);
    const d = [
      `M ${x1} ${y1}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2} ${y2}`,
      `L ${xi1} ${yi1}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${xi2} ${yi2}`,
      'Z',
    ].join(' ');
    return {
      key: s.key,
      label: s.label,
      value: s.value || 0,
      count: s.count,
      color: s.color || fallbackPalette[i % fallbackPalette.length],
      ratio,
      d,
      isOthers: !!s._isOthers,
    };
  });

  const handleEnter = (e, sl) => {
    setHovered(sl.key);
    const svg = e.currentTarget?.ownerSVGElement || null;
    const rect = svg?.getBoundingClientRect?.();
    setTooltip({
      label: sl.label,
      value: sl.value,
      count: sl.count,
      ratio: sl.ratio,
      x: rect ? (e.clientX - rect.left) : 0,
      y: rect ? (e.clientY - rect.top) : 0,
    });
  };
  const handleMove = (e) => {
    const svg = e.currentTarget?.ownerSVGElement || null;
    const rect = svg?.getBoundingClientRect?.();
    const x = rect ? (e.clientX - rect.left) : 0;
    const y = rect ? (e.clientY - rect.top) : 0;
    setTooltip(prev => prev ? { ...prev, x, y } : prev);
  };
  const handleLeave = () => {
    setHovered(null);
    setTooltip(null);
  };
  const handleClick = (sl) => {
    if (!onSliceClick || sl.isOthers) return;
    onSliceClick(sl.key);
  };

  return (
    <div className={`analysis-pie-chart ${darkMode ? 'dark-mode' : ''}`}>
      <div className="analysis-pie-svg-wrap" style={{ width: size, height: size }}>
        <svg width={size} height={size} role="img" aria-label={ariaLabel} className="analysis-pie-svg">
          {pathSlices.map(sl => (
            <path
              key={sl.key}
              d={sl.d}
              fill={sl.color}
              className={`analysis-pie-slice ${hovered === sl.key ? 'hovered' : ''} ${onSliceClick && !sl.isOthers ? 'clickable' : ''}`}
              onMouseEnter={(e) => handleEnter(e, sl)}
              onMouseMove={handleMove}
              onMouseLeave={handleLeave}
              onClick={() => handleClick(sl)}
            />
          ))}
          {/* Mittelschrift */}
          {(centerLabel || centerValue) && (
            <g pointerEvents="none">
              {centerValue && (
                <text x={cx} y={cy - 2} textAnchor="middle" className="analysis-pie-center-value">{centerValue}</text>
              )}
              {centerLabel && (
                <text x={cx} y={cy + 16} textAnchor="middle" className="analysis-pie-center-label">{centerLabel}</text>
              )}
            </g>
          )}
        </svg>
        {tooltip && (
          <div
            className="analysis-pie-tooltip"
            style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
          >
            <div className="analysis-pie-tooltip-label">{tooltip.label}</div>
            <div>{formatValue(tooltip.value)} ({(tooltip.ratio * 100).toFixed(1)} %)</div>
            {tooltip.count != null && <div>{tooltip.count} Messpunkt(e)</div>}
          </div>
        )}
      </div>
      <ul className="analysis-pie-legend">
        {pathSlices.map(sl => (
          <li
            key={sl.key}
            className={`analysis-pie-legend-item ${hovered === sl.key ? 'hovered' : ''} ${onSliceClick && !sl.isOthers ? 'clickable' : ''}`}
            onMouseEnter={() => setHovered(sl.key)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => handleClick(sl)}
          >
            <span className="analysis-pie-legend-color" style={{ backgroundColor: sl.color }} />
            <span className="analysis-pie-legend-label" title={sl.label}>{sl.label}</span>
            <span className="analysis-pie-legend-value">{formatValue(sl.value)}</span>
            <span className="analysis-pie-legend-pct">{(sl.ratio * 100).toFixed(1)} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default AnalysisPieChart;
